import jsPDF from 'jspdf';

/**
 * Generates a branded, single-incident PDF ("the actual report") with every
 * section appended: summary, overview, details, passengers, attachments,
 * acknowledgements + notes, reviewer comments and the event log.
 *
 * Client-side generation from the live incident object — no backend export
 * endpoint involved. Follows the jsPDF conventions used in cca-report.js.
 *
 * @param {Object} incident        The full incident document.
 * @param {Object} employeeNameMap Map of employeeId -> display name (optional).
 * @returns {boolean}              true when the PDF was produced.
 */

//#region palette (PRM Assist house style)
const NAVY = [27, 79, 138];    // #1B4F8A
const BLUE = [46, 134, 193];   // #2E86C1
const DARK = [51, 51, 51];     // #333
const MED = [102, 102, 102];   // #666
const LIGHT = [153, 153, 153]; // #999
const ROW_ALT = [234, 244, 251]; // #EAF4FB
const GREEN = [232, 245, 233];   // #E8F5E9
const GREEN_TXT = [46, 125, 50]; // #2E7D32
//#endregion

//#region date helpers — mirror IncidentDetail.js
const toJsDate = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    const d = new Date(ts);
    return isNaN(d) ? null : d;
};

const formatDate = (ts) => {
    const d = toJsDate(ts);
    return d ? d.toLocaleDateString('en-GB') : '—';
};

const formatDateTime = (ts) => {
    const d = toJsDate(ts);
    return d ? d.toLocaleString('en-GB') : '—';
};
//#endregion

const STATUS_LABELS = {
    open: 'Awaiting Acknowledgement',
    in_progress: 'Acknowledged — In Progress',
    fully_acknowledged: 'Acknowledged — Pending Approval',
    returned: 'Returned to Agent',
    closed: 'Approved & Complete',
};

const ACK_LABELS = { pending: 'Pending', acknowledged: 'Acknowledged' };

const INCIDENT_TYPE_LABELS = {
    'passenger-fall': 'Passenger Fall',
    'passenger-staff-incident': 'Passenger / Staff Incident',
    'airline-incident': 'Airline Incident',
    other: 'Other',
};

const MARGIN = 14;

export const generateIncidentReportPdf = (incident, employeeNameMap = {}) => {
    if (!incident) return false;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentWidth = pageWidth - MARGIN * 2;
    let y = 0;

    // Add a page break when the next block wouldn't fit, resetting y below the margin.
    const ensureSpace = (needed) => {
        if (y + needed > pageHeight - 18) {
            doc.addPage();
            y = 20;
        }
    };

    //#region primitives
    const bandTitle = () => {
        // Navy header band with report type + incident title.
        doc.setFillColor(...NAVY);
        doc.rect(0, 0, pageWidth, 26, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('INCIDENT REPORT', MARGIN, 13);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.text(doc.splitTextToSize(incident.title || 'Untitled', contentWidth), MARGIN, 20);
        y = 34;
    };

    const sectionHeading = (label) => {
        ensureSpace(14);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...BLUE);
        doc.text(label, MARGIN, y);
        y += 2.5;
        doc.setDrawColor(...NAVY);
        doc.setLineWidth(0.6);
        doc.line(MARGIN, y, pageWidth - MARGIN, y);
        y += 6;
        doc.setLineWidth(0.2);
    };

    // Wrapped body paragraph.
    const paragraph = (text) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...DARK);
        const lines = doc.splitTextToSize(String(text ?? '—') || '—', contentWidth);
        lines.forEach((line) => {
            ensureSpace(6);
            doc.text(line, MARGIN, y);
            y += 5;
        });
        y += 3;
    };

    // Two-column key/value grid for the summary block.
    const infoGrid = (pairs) => {
        const colW = contentWidth / 2;
        const labelH = 4.5;
        const valueGap = 5;
        for (let i = 0; i < pairs.length; i += 2) {
            const rowPairs = pairs.slice(i, i + 2);
            // Pre-compute wrapped values to size the row.
            const wrapped = rowPairs.map(([, v]) =>
                doc.splitTextToSize(String(v ?? '—') || '—', colW - 4)
            );
            const rowH = labelH + valueGap * Math.max(...wrapped.map((w) => w.length));
            ensureSpace(rowH + 2);
            rowPairs.forEach(([label], c) => {
                const x = MARGIN + c * colW;
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(...MED);
                doc.text(String(label).toUpperCase(), x, y);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(...DARK);
                doc.text(wrapped[c], x, y + labelH);
            });
            y += rowH + 2;
        }
        y += 2;
    };

    // Lightweight table: header row (navy) + zebra body rows with wrapped cells.
    const table = (headers, rows, colWidths, opts = {}) => {
        const { rowFill } = opts; // optional (rowData, colIndex) => [r,g,b] | null
        const cellPad = 2;
        const lineH = 4.4;

        const drawHeader = () => {
            doc.setFillColor(...NAVY);
            doc.rect(MARGIN, y, contentWidth, 8, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.setTextColor(255, 255, 255);
            let x = MARGIN;
            headers.forEach((h, i) => {
                doc.text(String(h).toUpperCase(), x + cellPad, y + 5.4);
                x += colWidths[i];
            });
            y += 8;
        };

        ensureSpace(20);
        drawHeader();

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        rows.forEach((row, rIdx) => {
            const wrapped = row.map((cell, i) =>
                doc.splitTextToSize(String(cell ?? '—') || '—', colWidths[i] - cellPad * 2)
            );
            const rowH = Math.max(lineH * Math.max(...wrapped.map((w) => w.length)) + 3, 8);

            if (y + rowH > pageHeight - 18) {
                doc.addPage();
                y = 20;
                drawHeader();
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
            }

            // Zebra background.
            if (rIdx % 2 === 1) {
                doc.setFillColor(...ROW_ALT);
                doc.rect(MARGIN, y, contentWidth, rowH, 'F');
            }

            let x = MARGIN;
            wrapped.forEach((cellLines, cIdx) => {
                const fill = rowFill && rowFill(row, cIdx);
                if (fill) {
                    doc.setFillColor(...fill);
                    doc.rect(x, y, colWidths[cIdx], rowH, 'F');
                }
                doc.setTextColor(...(fill ? GREEN_TXT : DARK));
                doc.text(cellLines, x + cellPad, y + 5);
                x += colWidths[cIdx];
            });
            y += rowH;
        });
        y += 4;
    };
    //#endregion

    //#region body
    bandTitle();

    // Summary
    sectionHeading('Summary');
    infoGrid([
        ['Incident Type', INCIDENT_TYPE_LABELS[incident.incidentType] ?? incident.incidentType],
        ['Status', STATUS_LABELS[incident.status] ?? incident.status],
        ['Incident Date', formatDate(incident.date)],
        ['Reported At', formatDateTime(incident.createdAt)],
        ['Flight Number', incident.flightNumber],
        ['Location', incident.location],
        ['Airport (IATA)', incident.iata],
        ['Report ID', incident.id ?? incident._id],
    ]);

    // Overview
    sectionHeading('Overview');
    paragraph(incident.description);

    // Details
    if (incident.issue || incident.actionTaken || incident.recommendation) {
        sectionHeading('Details');
        if (incident.issue) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...MED);
            ensureSpace(6); doc.text('ISSUE', MARGIN, y); y += 5;
            paragraph(incident.issue);
        }
        if (incident.actionTaken) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...MED);
            ensureSpace(6); doc.text('ACTION TAKEN', MARGIN, y); y += 5;
            paragraph(incident.actionTaken);
        }
        if (incident.recommendation) {
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...MED);
            ensureSpace(6); doc.text('RECOMMENDATION', MARGIN, y); y += 5;
            paragraph(incident.recommendation);
        }
    }

    // Passengers
    if (incident.passengers?.length > 0) {
        sectionHeading(`Passengers (${incident.passengers.length})`);
        table(
            ['Name', 'Seat', 'Assistance Requirements'],
            incident.passengers.map((p) => [
                [p.firstName, p.lastName].filter(Boolean).join(' ') || '—',
                p.seatNumber || '—',
                p.assistanceRequirements || '—',
            ]),
            [contentWidth * 0.4, contentWidth * 0.15, contentWidth * 0.45]
        );
    }

    // Attachments (listed, not embedded — matches the detail view)
    if (incident.attachments?.length > 0) {
        sectionHeading(`Attachments (${incident.attachments.length})`);
        table(
            ['File', 'Link'],
            incident.attachments.map((a) => [a.filename || '—', a.downloadUrl || '—']),
            [contentWidth * 0.4, contentWidth * 0.6]
        );
    }

    // Acknowledgements
    const ackEntries = Object.entries(incident.acknowledgements ?? {});
    sectionHeading(`Acknowledgements (${ackEntries.length})`);
    if (ackEntries.length === 0) {
        paragraph('No employees have been assigned to this incident yet.');
    } else {
        table(
            ['Employee', 'Status', 'Acknowledged At', 'Notes'],
            ackEntries.map(([empId, ack]) => [
                employeeNameMap[empId] || empId,
                ACK_LABELS[ack?.status] ?? ack?.status ?? '—',
                formatDateTime(ack?.acknowledgedAt),
                ack?.notes || 'No notes provided',
            ]),
            [contentWidth * 0.24, contentWidth * 0.16, contentWidth * 0.24, contentWidth * 0.36],
            {
                // Highlight acknowledged status cells in green.
                rowFill: (row, cIdx) => (cIdx === 1 && row[1] === 'Acknowledged' ? GREEN : null),
            }
        );
    }

    // Reviewer comments
    const comments = [...(incident.reviewComments ?? [])].sort(
        (a, b) => (toJsDate(a.timestamp)?.getTime() ?? 0) - (toJsDate(b.timestamp)?.getTime() ?? 0)
    );
    if (comments.length > 0) {
        sectionHeading(`Reviewer Comments (${comments.length})`);
        comments.forEach((c) => {
            ensureSpace(12);
            doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...NAVY);
            const who = c.author?.name || c.author?.email || 'Reviewer';
            doc.text(who, MARGIN, y);
            doc.setFont('helvetica', 'normal'); doc.setTextColor(...LIGHT);
            doc.text(formatDateTime(c.timestamp), pageWidth - MARGIN, y, { align: 'right' });
            y += 5;
            paragraph(c.content);
        });
    }

    // Event log
    const events = [...(incident.eventLog ?? [])].sort(
        (a, b) => (toJsDate(a.timestamp)?.getTime() ?? 0) - (toJsDate(b.timestamp)?.getTime() ?? 0)
    );
    if (events.length > 0) {
        sectionHeading(`Event Log (${events.length})`);
        table(
            ['Event', 'By', 'When'],
            events.map((ev) => [
                ev.action === 'edited'
                    ? `Edited (${(ev.fields ?? []).join(', ')})`
                    : STATUS_LABELS[ev.status] ?? ev.status ?? '—',
                ev.author?.name || ev.author?.email || '—',
                formatDateTime(ev.timestamp),
            ]),
            [contentWidth * 0.4, contentWidth * 0.3, contentWidth * 0.3]
        );
    }
    //#endregion

    //#region footer (every page)
    const now = new Date();
    const footerLabel = `PRM Assist  |  ${now.toLocaleString('en-GB', { month: 'long', year: 'numeric' })}`;
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...LIGHT);
        doc.text(footerLabel, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 10, { align: 'right' });
    }
    //#endregion

    const safeTitle = (incident.title || 'incident')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase()
        .slice(0, 40);
    doc.save(`incident-report-${safeTitle}-${now.toISOString().slice(0, 10)}.pdf`);
    return true;
};

export default generateIncidentReportPdf;
